<script setup lang="ts">
import { RightOutlined } from "@ant-design/icons-vue";
import LogoPng from "@main/assets/images/logo.png";
import { menuRoutes } from "@main/router/menuRoutes";
import { resetStores, useUserStore } from "@main/stores";
import { Modal, type MenuProps } from "ant-design-vue";
import { storeToRefs } from "pinia";
import { onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
	fetchBusinessList,
	type FetchBusinessListResponse,
} from "@/services/businessCenter/business";
import { loginOut } from "@/services/login";

// 组织切换逻辑
const userStore = useUserStore();
const { userInfo } = storeToRefs(userStore);
const activeCompanyId = ref(userStore.userInfo?.company.company_id || 0);
const businessList = ref<FetchBusinessListResponse>([]);

const getBusinessList = async () => {
	const res = await fetchBusinessList({ owner_type: 0 });

	if (res.code === window.$config.successCode) {
		businessList.value = res.data;

		if (!activeCompanyId.value) {
			activeCompanyId.value = res.data[0]?.company_id || 0;
		}
	}
};

const handleClickCompany: MenuProps["onClick"] = ({ key }) => {
	if (key === activeCompanyId.value) {
		return;
	}

	if (key === -1) {
		// @TODO: 跳转到创建/加入组织页面

		return;
	}

	Modal.confirm({
		title: "切换组织",
		type: "warning",
		content: "是否切换当前组织？切换后将重新加载页面，未保存的数据将丢失！",
		okText: "确认",
		cancelText: "取消",
		onOk: () => {
			userStore.switchOrganization(key as number);
		},
	});
};

onMounted(() => {
	getBusinessList();
});

// 菜单栏逻辑
const route = useRoute();
const router = useRouter();
const currentMenu = ref("");

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const placement = "right" as any;

const getItems = () => {
	return menuRoutes.map((route) => {
		const { path, meta, children } = route;

		const itemChildren = children?.map((child) => {
			const { path, meta } = child;
			const item = {
				key: path,
				label: meta?.title as string,
				title: meta?.title as string,
			};

			return item;
		});

		const item = {
			key: path,
			label: meta?.title as string,
			title: meta?.title as string,
			icon: meta?.icon as string,
			children: itemChildren,
		};

		return item;
	});
};

const items = getItems();

const handleClick = ({ key }) => {
	if (currentMenu.value === key) {
		return;
	}

	router.push(key);
};

const onClick: MenuProps["onClick"] = async ({ key }) => {
	if (key === 1) {
		try {
			await loginOut();
		} catch (_error) {
			//
		} finally {
			sessionStorage.clear();
			localStorage.clear();
			resetStores();
			router.replace("/login");
		}
	}
};

watch(
	() => route,
	(value) => {
		const matchedItem = items.find((item) => value.matched.some((m) => m.path === item.key));

		currentMenu.value = matchedItem ? matchedItem.key : items[0].key;
	},
	{ immediate: true, deep: true }
);

// 菜单收起/展开逻辑
const collapsed = ref(true);

const toggleCollapsed = () => {
	collapsed.value = !collapsed.value;
};
</script>

<template>
	<div
		class="main-sider-menu pb-md pt-5xh relative isolate flex shrink-0 flex-col items-center overflow-hidden bg-white/40 transition-[width] duration-350 ease-in-out"
		:class="{
			'w-160': !collapsed,
			'w-xxh': collapsed,
		}"
	>
		<a-tooltip v-if="collapsed" placement="right" :title="userInfo?.company.name">
			<div class="mb-xxh w-full">
				<div class="gap-ms flex w-full cursor-pointer flex-col items-center" @click.prevent>
					<a-dropdown :trigger="['click']" placement="bottomLeft">
						<img class="w-hs h-hs rounded-ml" :src="LogoPng" alt="" />
						<template #overlay>
							<a-menu class="!p-smm !py-smm" @click="handleClickCompany">
								<a-menu-item
									v-for="item in businessList"
									:key="item.company_id"
									class="!px-smm !py-sm"
								>
									<div class="gap-md flex items-center">
										<img class="h-lg w-lg" :src="item.logo" alt="" />

										<div class="w-en">
											<div class="text-text-primary line-clamp-1 text-base font-medium">
												{{ item.name }}
											</div>
										</div>

										<span
											v-if="item.company_id === activeCompanyId"
											class="w-xxh bg-soft rounded-2xl text-center text-sm leading-[22px] text-black/85"
										>
											当前
										</span>
									</div>
								</a-menu-item>
								<a-menu-item :key="-1" class="!p-smm">
									<div class="gap-md flex items-center justify-between">
										<div class="flex-row-center gap-md">
											<img
												class="h-lg w-lg"
												src="@main/assets/images/layout/architecture.png"
												alt=""
											/>

											<div class="text-text-primary line-clamp-1 text-base font-medium">
												创建或加入组织 / 企业
											</div>
										</div>

										<RightOutlined class="!font-normal" />
									</div>
								</a-menu-item>
							</a-menu>
						</template>
					</a-dropdown>
				</div>
			</div>
		</a-tooltip>

		<div v-else class="mb-[68px] w-full">
			<div class="gap-ms flex w-full cursor-pointer flex-col items-center" @click.prevent>
				<a-dropdown :trigger="['click']" placement="bottomLeft">
					<div class="flex-col-center">
						<img class="w-hm h-hm rounded-ml mb-ms" :src="LogoPng" alt="" />
						<div class="line-clamp-1 text-xl">
							{{ userInfo?.company.name }}
						</div>
					</div>

					<template #overlay>
						<a-menu class="!px-smm !py-mms" @click="handleClickCompany">
							<a-menu-item
								v-for="item in businessList"
								:key="item.company_id"
								class="!px-smm !py-sm"
							>
								<div class="gap-md flex items-center">
									<img class="h-lg w-lg" :src="item.logo" alt="" />

									<div class="w-en">
										<div class="text-text-primary line-clamp-1 text-base font-medium">
											{{ item.name }}
										</div>
									</div>

									<span
										v-if="item.company_id === activeCompanyId"
										class="w-xxh bg-soft rounded-2xl text-center text-sm leading-[22px] text-black/85"
									>
										当前
									</span>
								</div>
							</a-menu-item>
							<a-menu-item :key="-1" class="!p-smm">
								<div class="gap-md flex items-center justify-between">
									<div class="flex-row-center gap-md">
										<img
											class="h-lg w-lg"
											src="@main/assets/images/layout/architecture.png"
											alt=""
										/>

										<div class="text-text-primary line-clamp-1 text-base font-medium">
											创建或加入组织 / 企业
										</div>
									</div>

									<RightOutlined class="!font-normal" />
								</div>
							</a-menu-item>
						</a-menu>
					</template>
				</a-dropdown>
			</div>
		</div>

		<div class="gap-lh flex w-full flex-1 shrink-0 flex-col overflow-y-auto">
			<template v-for="(item, index) in items" :key="index">
				<a-tooltip v-if="collapsed" placement="right" :title="item.title">
					<div
						class="gap-md h-hm flex w-full cursor-pointer items-center justify-center text-white hover:bg-white/20"
						@click="handleClick(item)"
					>
						<div
							class="w-lh h-lh flex items-center justify-center rounded-lg"
							:class="{
								'shadow-menu bg-white': currentMenu === item.key,
							}"
						>
							<i
								class="iconfont !text-3xl text-black"
								:class="{
									[item.icon]: true,
									'!text-primary': currentMenu === item.key,
								}"
							></i>
						</div>
					</div>
				</a-tooltip>

				<div
					v-else
					class="gap-md h-hm flex w-full cursor-pointer items-center justify-center text-white hover:bg-white/20"
					@click="handleClick(item)"
				>
					<div
						class="w-lh h-lh flex items-center justify-center rounded-lg"
						:class="{
							'shadow-menu bg-white': currentMenu === item.key,
						}"
					>
						<i
							class="iconfont !text-3xl text-black"
							:class="{
								[item.icon]: true,
								'!text-primary': currentMenu === item.key,
							}"
						></i>
					</div>

					<span
						v-show="!collapsed"
						class="text-text-menu line-clamp-1 text-base transition-[opacity,transform] duration-200 ease-in-out"
						:class="{
							'font-semibold': currentMenu === item.key,
							'pointer-events-none -translate-x-2 opacity-0': collapsed,
							'translate-x-0 opacity-100': !collapsed,
						}"
					>
						{{ item.title }}
					</span>
				</div>
			</template>

			<a-tooltip v-if="collapsed" placement="right" title="个人中心">
				<div
					class="gap-md h-hm flex w-full cursor-pointer items-center justify-center text-white hover:bg-white/20"
				>
					<a-dropdown :trigger="['click']">
						<div class="w-lh h-lh flex items-center justify-center rounded-lg">
							<i class="iconfont zxg-personal !text-3xl text-black"></i>
						</div>
						<template #overlay>
							<a-menu class="!px-smm" @click="onClick">
								<a-menu-item :key="1" class="!px-smm !py-sm">
									<span>退出登录</span>
								</a-menu-item>
							</a-menu>
						</template>
					</a-dropdown>
				</div>
			</a-tooltip>

			<a-dropdown v-else :trigger="['click']" :placement="placement">
				<div
					class="gap-md h-hm flex w-full cursor-pointer items-center justify-center text-white hover:bg-white/20"
				>
					<div class="w-lh h-lh flex items-center justify-center rounded-lg">
						<i class="iconfont zxg-personal !text-3xl text-black"></i>
					</div>

					<span
						v-show="!collapsed"
						class="text-text-menu line-clamp-1 text-base transition-[opacity,transform] duration-200 ease-in-out"
						:class="{
							'pointer-events-none -translate-x-2 opacity-0': collapsed,
							'translate-x-0 opacity-100': !collapsed,
						}"
					>
						个人中心
					</span>
				</div>
				<template #overlay>
					<a-menu class="!px-smm" @click="onClick">
						<a-menu-item :key="1" class="!px-smm !py-sm">
							<span>退出登录</span>
						</a-menu-item>
					</a-menu>
				</template>
			</a-dropdown>
		</div>

		<div class="pr-md flex w-full justify-end">
			<i
				v-if="!collapsed"
				class="iconfont zxg-expand !text-text-menu cursor-pointer !text-3xl"
				@click="toggleCollapsed"
			/>

			<i
				v-else
				class="iconfont zxg-collapse !text-text-menu cursor-pointer !text-3xl"
				@click="toggleCollapsed"
			/>
		</div>
	</div>
</template>

<style scoped lang="scss">
.main-sider-menu {
	backdrop-filter: blur(32px) saturate(160%);
	-webkit-backdrop-filter: blur(32px) saturate(160%);
}
</style>
